import React, { ChangeEvent, useCallback, useContext, useEffect, useMemo, useState } from "react"

import { SuccessContext } from "../../../../../../../../../components/layout/success-snackbar"
import { Children } from "../../../../../../../../../components/miscellianous/children"
import { BSInputContext } from "../../../../../../../../../core/context/beem-shot/BSInput/BSInputContext"
import { BSInput } from "../../../../../../../../../core/dto/beem-shot/BSInput/BSInput"
import { BSInputUsageUpdateDto } from "../../../../../../../../../core/dto/beem-shot/BSInput/BSInputUsageUpdateDto"
import { FlatTypeEnum } from "../../../../../../../../../core/enum/beem-shot/FlatTypeEnum"
import { OfficeTypeEnum } from "../../../../../../../../../core/enum/beem-shot/OfficeTypeEnum"
import { SchoolTypeEnum } from "../../../../../../../../../core/enum/beem-shot/SchoolTypeEnum"
import { UsageEnum } from "../../../../../../../../../core/enum/usageEnum"
import { useForm } from "../../../../../../../../../core/hooks/form/use-form"
import {
  nonZero,
  nonZerodAtLeastOne,
  required,
  sumEqualTo100,
  ValidationRule,
} from "../../../../../../../../../core/hooks/form/validation"
import { BSBuildingContext } from "../../../components/context/BSBuildingContext"

/** V0 Temporary component in order to enable calculation with main usage
 * When multi usage, it will be affected according to level*/
export interface IBSUsageForm {
  usage: UsageEnum
  typeBureau: OfficeTypeEnum
  nombreLogement: boolean
  numberT1: number
  numberT2: number
  numberT3: number
  numberT4: number
  numberT5: number
  typeLogement: FlatTypeEnum
  typeEnseignement: SchoolTypeEnum
  surfaceUnitaireBureaux: number
  pourcentageCloisonne: number
  pourcentageOpenSpace: number
  nombreEtage: number
  hauteurMoyenneEtage: number
}

function bsInputUsageDtoFormToUpdate(form: IBSUsageForm, bsInputId: string): BSInputUsageUpdateDto {
  return {
    id: bsInputId,
    usage: form.usage,
    typeBureau: form.typeBureau,
    nombreLogement: form.nombreLogement,
    numberT1: form.numberT1,
    numberT2: form.numberT2,
    numberT3: form.numberT3,
    numberT4: form.numberT4,
    numberT5: form.numberT5,
    typeLogement: form.typeLogement,
    typeEnseignement: form.typeEnseignement,
    surfaceUnitaireBureaux: form.surfaceUnitaireBureaux,
    pourcentageCloisonne: form.pourcentageCloisonne,
    pourcentageOpenSpace: form.pourcentageOpenSpace,
    nombreEtage: form.nombreEtage,
    hauteurMoyenneEtage: form.hauteurMoyenneEtage,
  }
}

export function bsInputUsageDtoToForm(dto: BSInput | undefined): IBSUsageForm {
  return dto
    ? {
        usage: dto.usage,
        typeBureau: dto.typeBureau,
        nombreLogement: dto.nombreLogement,
        numberT1: dto.numberT1,
        numberT2: dto.numberT2,
        numberT3: dto.numberT3,
        numberT4: dto.numberT4,
        numberT5: dto.numberT5,
        typeLogement: dto.typeLogement,
        typeEnseignement: dto.typeEnseignement,
        surfaceUnitaireBureaux: dto.surfaceUnitaireBureaux,
        pourcentageCloisonne: dto.pourcentageCloisonne,
        pourcentageOpenSpace: dto.pourcentageOpenSpace,
        nombreEtage: dto.nombreEtage,
        hauteurMoyenneEtage: dto.hauteurMoyenneEtage,
      }
    : ({
        usage: UsageEnum.LOGEMENTS_COLLECTIFS,
        typeBureau: OfficeTypeEnum.CLOISONNE,
        nombreLogement: false,
        numberT1: 0,
        numberT2: 0,
        numberT3: 0,
        numberT4: 0,
        numberT5: 0,
        typeLogement: FlatTypeEnum.LOGEMENTS_MIXTE,
        typeEnseignement: SchoolTypeEnum.ECOLE_PRIMAIRE,
        surfaceUnitaireBureaux: 0,
        pourcentageCloisonne: 0,
        pourcentageOpenSpace: 0,
        nombreEtage: 0,
        hauteurMoyenneEtage: 0,
      } as IBSUsageForm)
}

export const BSUsageContext = React.createContext<BSUsageStore>({} as BSUsageStore)

export function BSUsageContextProvider({ children }: Readonly<Children>): React.JSX.Element {
  const { bsInput, updateBSInputUsage } = useContext(BSInputContext)
  const { selectedBSBuilding } = useContext(BSBuildingContext)
  const openSuccessSnackbar = useContext(SuccessContext)

  const [isUsageUpdating, setIsUsageUpdating] = useState<boolean>(false)

  const submit: (form: IBSUsageForm) => Promise<BSInput | undefined> = useCallback(
    (form) => {
      if (bsInput) {
        return updateBSInputUsage(bsInputUsageDtoFormToUpdate(form, bsInput.id)).then((response) => {
          openSuccessSnackbar("L'usage principal a été enregistré.")
          setIsUsageUpdating(false)
          return response
        })
      }

      return Promise.resolve(undefined)
    },
    [bsInput, openSuccessSnackbar, updateBSInputUsage]
  )

  const { form, errors, handleChange, handleSubmit, isSubmitting, setRules } = useForm(
    bsInput,
    bsInputUsageDtoToForm,
    [required("usage")],
    submit,
    () => setIsUsageUpdating(true)
  )

  useEffect(() => {
    const rules: ValidationRule<IBSUsageForm>[] = []
    if (form.usage === UsageEnum.LOGEMENTS_COLLECTIFS) {
      rules.push(
        ...[
          required("typeLogement"),
          required("nombreLogement"),
          required("numberT1"),
          required("numberT2"),
          required("numberT3"),
          required("numberT4"),
          required("numberT5"),
        ]
      )
    }

    if (form.nombreLogement) {
      rules.push(...nonZerodAtLeastOne(["numberT1", "numberT2", "numberT3", "numberT4", "numberT5"]))
    }

    if (form.usage === UsageEnum.BUREAUX) {
      rules.push(...[required("typeBureau")])

      if (form.typeBureau === OfficeTypeEnum.MIXTE || form.typeBureau === OfficeTypeEnum.CLOISONNE) {
        rules.push(...[nonZero("surfaceUnitaireBureaux")])
      }

      if (form.typeBureau === OfficeTypeEnum.MIXTE) {
        rules.push(
          ...[
            sumEqualTo100("pourcentageCloisonne", "pourcentageOpenSpace"),
            sumEqualTo100("pourcentageOpenSpace", "pourcentageCloisonne"),
          ]
        )
      }
    }

    if (selectedBSBuilding?.bim) {
      rules.push(...[required("nombreEtage"), required("hauteurMoyenneEtage")])
    }

    setRules(rules)
  }, [form.nombreLogement, form.typeBureau, form.usage, selectedBSBuilding?.bim, setRules])

  const usageStore = useMemo(
    () => ({ form, handleChange, handleSubmit, errors, isSubmitting, isUsageUpdating }),
    [form, handleChange, handleSubmit, errors, isSubmitting, isUsageUpdating]
  )
  return <BSUsageContext.Provider value={usageStore}>{children}</BSUsageContext.Provider>
}

export interface BSUsageStore {
  form: IBSUsageForm
  errors: Record<string, string | undefined>
  isSubmitting: boolean
  isUsageUpdating: boolean

  handleChange(event: ChangeEvent<HTMLInputElement>): void

  handleSubmit(
    event?: React.FormEvent<HTMLFormElement> | React.FormEvent<HTMLDivElement>,
    shouldResetForm?: boolean
  ): Promise<void>
}
